'use client';

import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '@/config/theme';
import { ChevronIcon } from '@/components/icons/ChevronIcon';

/**
 * Accordion Component Props
 */
export interface AccordionProps {
  /** Title displayed in the accordion header - can be string or React element */
  title: string | React.ReactNode;
  /** Content to be displayed when accordion is expanded */
  children: React.ReactNode;
  /** Optional CSS class name */
  className?: string;
  /** Initial collapsed state */
  defaultCollapsed?: boolean;
  /** Controlled collapsed state */
  collapsed?: boolean;
  /** Callback when collapsed state changes */
  onToggle?: (collapsed: boolean) => void;
}

// ============================================================================
// Styled Components
// ============================================================================

const AccordionContainer = styled.div`
  padding: 10px;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.gray[200]};
  border-radius: 12px;
  box-shadow: ${theme.shadows.sm};
  overflow: visible;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    box-shadow: ${theme.shadows.md};
    border-color: ${theme.colors.border.tertiary};
  }
`;

const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: ${theme.colors.white};
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  border-radius: 12px 12px 0 0;

  &:hover {
    background: ${theme.colors.gray[50]};
  }

  &:active {
    background: ${theme.colors.gray[100]};
  }
`;

const AccordionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.gray[900]};
  margin: 0;
  flex: 1;
  
  /* Support for string titles */
  &:empty::before {
    content: attr(data-title);
  }
`;

const CollapseIcon = styled.div<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: ${theme.colors.gray[50]};
  border: 1px solid ${theme.colors.gray[200]};
  border-radius: 8px;
  color: ${theme.colors.gray[500]};
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${theme.colors.gray[100]};
    border-color: ${theme.colors.border.tertiary};
    color: ${theme.colors.gray[700]};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform: ${({ $expanded }) => ($expanded ? 'rotate(0deg)' : 'rotate(180deg)')};
  }
`;

const AccordionContent = styled.div<{ $collapsed: boolean }>`
  max-height: ${({ $collapsed }) => ($collapsed ? '0' : '2000px')};
  opacity: ${({ $collapsed }) => ($collapsed ? '0' : '1')};
  overflow: ${({ $collapsed }) => ($collapsed ? 'hidden' : 'visible')};
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), 
              opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

// ============================================================================
// Component
// ============================================================================

/**
 * Accordion Component
 * 
 * A reusable accordion/collapsible panel component with smooth animations.
 * Can be used in controlled or uncontrolled mode.
 * 
 * @component
 * @example
 * ```tsx
 * // Uncontrolled
 * <Accordion title="Section Title" defaultCollapsed={false}>
 *   <div>Content goes here</div>
 * </Accordion>
 * 
 * // Controlled
 * <Accordion 
 *   title="Section Title" 
 *   collapsed={isCollapsed}
 *   onToggle={setIsCollapsed}
 * >
 *   <div>Content goes here</div>
 * </Accordion>
 * ```
 */
export const Accordion: React.FC<AccordionProps> = ({
  title,
  children,
  className,
  defaultCollapsed = false,
  collapsed: controlledCollapsed,
  onToggle,
}) => {
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  
  // Use controlled state if provided, otherwise use internal state
  const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed;
  
  const handleToggle = () => {
    const newCollapsed = !isCollapsed;
    if (controlledCollapsed === undefined) {
      setInternalCollapsed(newCollapsed);
    }
    onToggle?.(newCollapsed);
  };

  return (
    <AccordionContainer className={className}>
      <AccordionHeader onClick={handleToggle}>
        <AccordionTitle>{title}</AccordionTitle>
        <CollapseIcon
          $expanded={!isCollapsed}
          aria-expanded={!isCollapsed ? 'true' : 'false'}
          aria-label={isCollapsed ? `Expand ${title}` : `Collapse ${title}`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggle();
            }
          }}
        >
          <ChevronIcon />
        </CollapseIcon>
      </AccordionHeader>

      <AccordionContent $collapsed={isCollapsed}>
        {children}
      </AccordionContent>
    </AccordionContainer>
  );
};

export default Accordion;
